import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  LinearProgress,
  Collapse,
  InputAdornment,
  Switch,
  FormControlLabel,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  AccountBalance as GLIcon,
  Receipt as ExpenseIcon,
  Assessment as ReportIcon,
  AccountBalanceWallet as AccountIcon,
  TrendingUp as ProfitIcon,
  TrendingDown as LossIcon,
  AttachMoney as MoneyIcon,
  Assignment as ZOutIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Check as ApprovalIcon,
  Close as RejectIcon,
  Send as SubmitIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Print as PrintIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  CreditCard as PaymentIcon,
  Calculate as CalculatorIcon,
  Business as VendorIcon,
  PieChart as ChartIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  MonetizationOn as RevenueIcon,
  ShoppingCart as PurchaseIcon,
  LocalAtm as CashIcon,
  CreditCardOff as CreditIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getColor = () => {
    switch (status) {
      case 'posted': return { bg: '#4caf50', color: '#fff' };
      case 'draft': return { bg: '#ff9800', color: '#fff' };
      case 'approved': return { bg: '#2196f3', color: '#fff' };
      case 'rejected': return { bg: '#f44336', color: '#fff' };
      case 'pending_approval': return { bg: '#9c27b0', color: '#fff' };
      case 'finalized': return { bg: '#8bc34a', color: '#fff' };
      default: return { bg: '#e0e0e0', color: '#000' };
    }
  };

  const colors = getColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
  };
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function FinancialPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [glEntries, setGlEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [zOuts, setZOuts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
    status: 'all',
    accountType: 'all',
    category: 'all',
    search: ''
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalEntries, setTotalEntries] = useState(0);

  // Financial summary
  const [financialSummary, setFinancialSummary] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netIncome: 0,
    cashBalance: 0,
    accountsReceivable: 0,
    accountsPayable: 0,
    pendingApprovals: 0
  });

  // Reports data
  const [trialBalance, setTrialBalance] = useState({ accounts: [], totals: { debits: 0, credits: 0 } });
  const [incomeStatement, setIncomeStatement] = useState<any>(null);
  const [balanceSheet, setBalanceSheet] = useState<any>(null);

  useEffect(() => {
    fetchFinancialData();
  }, [currentTab, filters, page, rowsPerPage]);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      switch (currentTab) {
        case 0: // Dashboard
          await fetchFinancialSummary();
          break;
        case 1: // Chart of Accounts
          await fetchChartOfAccounts();
          break;
        case 2: // General Ledger
          await fetchGLEntries();
          break;
        case 3: // Expenses
          await fetchExpenses();
          break;
        case 4: // Z-Out
          await fetchZOuts();
          break;
        case 5: // Reports
          await fetchReports();
          break;
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialSummary = async () => {
    // This would fetch summary data from multiple endpoints
    setFinancialSummary({
      totalRevenue: 45750.00,
      totalExpenses: 32100.00,
      netIncome: 13650.00,
      cashBalance: 25000.00,
      accountsReceivable: 8500.00,
      accountsPayable: 12300.00,
      pendingApprovals: 5
    });
  };

  const fetchChartOfAccounts = async () => {
    try {
      const response = await api.get('/financial/chart-of-accounts');
      setChartOfAccounts(response.data);
    } catch (error) {
      console.error('Error fetching chart of accounts:', error);
    }
  };

  const fetchGLEntries = async () => {
    try {
      const params = new URLSearchParams({
        startDate: filters.startDate.toISOString(),
        endDate: filters.endDate.toISOString(),
        status: filters.status,
        accountType: filters.accountType,
        search: filters.search,
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });

      const response = await api.get('/financial/general-ledger', { params: {
        startDate: filters.startDate.toISOString(),
        endDate: filters.endDate.toISOString(),
        status: filters.status,
        accountType: filters.accountType,
        search: filters.search,
        page: page + 1,
        limit: rowsPerPage
      }});
      
      setGlEntries(response.data.entries);
      setTotalEntries(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching GL entries:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const params = new URLSearchParams({
        startDate: filters.startDate.toISOString(),
        endDate: filters.endDate.toISOString(),
        status: filters.status,
        accountType: filters.accountType,
        search: filters.search,
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });

      const response = await api.get('/financial/expenses', { params: {
        startDate: filters.startDate.toISOString(),
        endDate: filters.endDate.toISOString(),
        status: filters.status,
        category: filters.category,
        search: filters.search,
        page: page + 1,
        limit: rowsPerPage
      }});
      
      setExpenses(response.data.expenses);
      setTotalEntries(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchZOuts = async () => {
    try {
      const response = await api.get('/financial/z-out');
      setZOuts(response.data);
    } catch (error) {
      console.error('Error fetching Z-Outs:', error);
    }
  };

  const fetchReports = async () => {
    try {
      // Fetch trial balance
      const trialBalanceResponse = await api.get('/financial/reports/trial-balance/location1');
      setTrialBalance(trialBalanceResponse.data);

      // Fetch income statement
      const incomeResponse = await api.get('/financial/reports/income-statement/location1', {
        params: {
          startDate: filters.startDate.toISOString(),
          endDate: filters.endDate.toISOString()
        }
      });
      setIncomeStatement(incomeResponse.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const renderDashboard = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                <RevenueIcon />
              </Avatar>
              <Typography variant="h6">Total Revenue</Typography>
            </Box>
            <Typography variant="h4" color="success.main">
              ${financialSummary.totalRevenue.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current month
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                <PurchaseIcon />
              </Avatar>
              <Typography variant="h6">Total Expenses</Typography>
            </Box>
            <Typography variant="h4" color="error.main">
              ${financialSummary.totalExpenses.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current month
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <ProfitIcon />
              </Avatar>
              <Typography variant="h6">Net Income</Typography>
            </Box>
            <Typography variant="h4" color="primary.main">
              ${financialSummary.netIncome.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current month
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                <CashIcon />
              </Avatar>
              <Typography variant="h6">Cash Balance</Typography>
            </Box>
            <Typography variant="h4" color="info.main">
              ${financialSummary.cashBalance.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              As of today
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Financial Overview
          </Typography>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography>Accounts Receivable</Typography>
              <Typography variant="h6" color="primary">
                ${financialSummary.accountsReceivable.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography>Accounts Payable</Typography>
              <Typography variant="h6" color="error">
                ${financialSummary.accountsPayable.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography>Pending Approvals</Typography>
              <Badge badgeContent={financialSummary.pendingApprovals} color="warning">
                <Typography variant="h6">
                  {financialSummary.pendingApprovals} items
                </Typography>
              </Badge>
            </Box>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Stack spacing={2}>
            <Button variant="outlined" startIcon={<AddIcon />} fullWidth>
              Create GL Entry
            </Button>
            <Button variant="outlined" startIcon={<ExpenseIcon />} fullWidth>
              Add Expense
            </Button>
            <Button variant="outlined" startIcon={<ZOutIcon />} fullWidth>
              Create Z-Out
            </Button>
            <Button variant="outlined" startIcon={<ReportIcon />} fullWidth>
              Generate Report
            </Button>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderChartOfAccounts = () => (
    <Paper>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search accounts..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={filters.accountType}
                onChange={(e) => handleFilterChange('accountType', e.target.value)}
                label="Account Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="asset">Assets</MenuItem>
                <MenuItem value="liability">Liabilities</MenuItem>
                <MenuItem value="equity">Equity</MenuItem>
                <MenuItem value="revenue">Revenue</MenuItem>
                <MenuItem value="expense">Expenses</MenuItem>
                <MenuItem value="cogs">COGS</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={7}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<AddIcon />}>
                Add Account
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Account Code</TableCell>
              <TableCell>Account Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              chartOfAccounts.map((account: any) => (
                <TableRow key={account._id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontFamily="monospace">
                      {account.accountCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {account.accountName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={account.accountType} />
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={account.currentBalance >= 0 ? 'success.main' : 'error.main'}
                    >
                      ${Math.abs(account.currentBalance).toLocaleString()}
                      {account.currentBalance < 0 && ' (CR)'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      color={account.isActive ? 'success' : 'default'}
                      label={account.isActive ? 'Active' : 'Inactive'} 
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderGLEntries = () => (
    <Paper>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <DatePicker
              label="Start Date"
              value={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <DatePicker
              label="End Date"
              value={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="posted">Posted</MenuItem>
                <MenuItem value="reversed">Reversed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={6}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<AddIcon />}>
                New Entry
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Entry #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Debits</TableCell>
              <TableCell>Credits</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              glEntries.map((entry: any) => (
                <TableRow key={entry._id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontFamily="monospace">
                      {entry.entryNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(entry.transactionDate).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.reference}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="success.main">
                      ${entry.totalDebits.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="error.main">
                      ${entry.totalCredits.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip size="small" status={entry.status} label={entry.status} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {entry.status === 'draft' && (
                        <Tooltip title="Post Entry">
                          <IconButton size="small">
                            <ApprovalIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalEntries}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  const renderReports = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Trial Balance
          </Typography>
          {trialBalance.accounts.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell align="right">Debit</TableCell>
                    <TableCell align="right">Credit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trialBalance.accounts.slice(0, 10).map((account: any) => (
                    <TableRow key={account._id}>
                      <TableCell>
                        <Typography variant="body2">
                          {account.accountCode} - {account.accountName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {account.debitBalance > 0 && `$${account.debitBalance.toLocaleString()}`}
                      </TableCell>
                      <TableCell align="right">
                        {account.creditBalance > 0 && `$${account.creditBalance.toLocaleString()}`}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell><strong>Total</strong></TableCell>
                    <TableCell align="right">
                      <strong>${trialBalance.totals.debits.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>${trialBalance.totals.credits.toLocaleString()}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No trial balance data available</Alert>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Income Statement Summary
          </Typography>
          {incomeStatement ? (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Total Revenue:</Typography>
                <Typography color="success.main">
                  ${incomeStatement.revenue.total.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Total Expenses:</Typography>
                <Typography color="error.main">
                  ${incomeStatement.expenses.total.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Cost of Goods Sold:</Typography>
                <Typography color="error.main">
                  ${incomeStatement.cogs.total.toLocaleString()}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Net Income:</Typography>
                <Typography 
                  variant="h6" 
                  color={incomeStatement.netIncome >= 0 ? 'success.main' : 'error.main'}
                >
                  ${incomeStatement.netIncome.toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Alert severity="info">No income statement data available</Alert>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Generate Reports
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth startIcon={<PrintIcon />}>
                Trial Balance
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth startIcon={<PrintIcon />}>
                Income Statement
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth startIcon={<PrintIcon />}>
                Balance Sheet
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth startIcon={<PrintIcon />}>
                Cash Flow
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Financial Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive financial tracking with GL, expenses, and reporting
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Dashboard" />
          <Tab label="Chart of Accounts" />
          <Tab label="General Ledger" />
          <Tab label="Expenses" />
          <Tab label="Z-Out Reports" />
          <Tab label="Financial Reports" />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {renderDashboard()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderChartOfAccounts()}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {renderGLEntries()}
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Expense management interface coming soon! Will include expense entry, approval workflow, and vendor management.
        </Alert>
      </TabPanel>

      <TabPanel value={currentTab} index={4}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Z-Out (end-of-day) reporting interface coming soon! Will include daily sales summaries, cash reconciliation, and performance metrics.
        </Alert>
      </TabPanel>

      <TabPanel value={currentTab} index={5}>
        {renderReports()}
      </TabPanel>
    </Container>
  );
}